import React, { useState, useContext } from 'react';
import { Button, Input, Alert } from '@bnvsrnd/brand-assets';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { loginSchema } from '../../utils/validationSchemas';
import api from '../../utils/api';
import { trackEvent } from '../../utils/visitorTracker';
import StudentLoginForm from '../Student/StudentLoginForm';
import ConsentModal from './ConsentModal';
import './Login.css';
import logo from '../../assets/images/Duotutor_logo_black.svg';

function Login() {
    const { login: authLogin, updateUser } = useContext(AuthContext);
    const [error, setError] = useState('');
    // 'user' = 일반 로그인, 'student' = 학생 로그인 (같은 화면 안에서 전환)
    // /login?mode=student 이거나 이 브라우저에 반코드 입장 기록(guestAuth)이 있으면 학생 로그인부터 보여 준다(재접속).
    const [mode, setMode] = useState(() => {
        try {
            if (new URLSearchParams(window.location.search).get('mode') === 'student') return 'student';
            return localStorage.getItem('guestAuth') ? 'student' : 'user';
        } catch {
            return 'user';
        }
    });
    const navigate = useNavigate();
    const location = useLocation();

    // 아직 최신 이용약관에 동의한 적 없는 계정이 로그인을 시도하면 서버가 428을 준다 —
    // 자격증명은 이미 맞았으니, 동의를 받아서 같은 자격증명으로 로그인만 다시 시도하면 된다.
    const [consentState, setConsentState] = useState(null); // { credentials, policyGroup } | null
    const [consentSubmitting, setConsentSubmitting] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting }
    } = useForm({
        resolver: yupResolver(loginSchema),
        mode: 'onBlur'
    });

    const goToDestinationAfterLogin = async (user) => {
        const u = user || {};
        // 방문자 분석: 같은 브라우저 UUID 의 이전 행동을 이 계정과 잇는다.
        trackEvent('login_success', { userId: u.id || u._id || '', role: u.role || '' });
        await updateUser();
        let dest = '/dashboard';
        if (u.isAdmin) {
            dest = '/admin/overview';
        } else if ((u.roleLevel || 0) >= 3) {
            dest = '/instructor/dashboard'; // 멘토/교사 → 멘토 포털을 메인으로
        }
        // 보호된 페이지에 비로그인 상태로 접근했다가 로그인한 경우(예: OAuth 동의 화면)
        // 원래 가려던 곳으로 돌아간다 — ProtectedRoute 가 state.from 에 남겨둔 위치.
        const from = location.state?.from;
        if (from?.pathname) {
            dest = `${from.pathname}${from.search || ''}`;
        }
        navigate(dest);
    };

    const onSubmit = async (data) => {
        setError('');

        try {
            const result = await authLogin(data);
            if (result.success) {
                await goToDestinationAfterLogin(result.user);
            }
        } catch (err) {
            const res = err.response;
            if (res?.status === 428 && res.data?.code === 'CONSENT_REQUIRED') {
                setConsentState({ credentials: data, policyGroup: res.data.policyGroup || 'instructor' });
                return;
            }
            setError(res?.data?.message || '로그인에 실패했습니다.');
        }
    };

    const handleConsentAgree = async () => {
        if (!consentState) return;
        setConsentSubmitting(true);
        try {
            const result = await authLogin({
                ...consentState.credentials,
                agreedToTerms: true,
                agreedToPrivacy: true
            });
            if (result.success) {
                setConsentState(null);
                await goToDestinationAfterLogin(result.user);
            }
        } catch (err) {
            setError(err.response?.data?.message || '로그인에 실패했습니다.');
            setConsentState(null);
        } finally {
            setConsentSubmitting(false);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleResendVerification = async (email) => {
        try {
            const response = await api.post('/auth/resend-verification', { email });
            setError(response.data.message || '인증 메일을 다시 보냈습니다. 이메일 확인 후 다시 로그인해 주세요.');
        } catch (err) {
            setError('인증 메일 재발송에 실패했습니다. 잠시 후 다시 시도해주세요.');
        }
    };

    return (
        <div className="login-container">
            <div className="login-form-wrapper">
                {/* 메인(온보딩) 화면으로 돌아가기 */}
                <div style={{ width: '100%', marginBottom: 'var(--space-3)' }}>
                    <Link
                        to="/main"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            color: 'var(--text-secondary, #7a7f8a)',
                            font: 'var(--body-small)',
                            textDecoration: 'none',
                        }}
                    >
                        <ArrowLeft size={16} /> 메인 화면으로
                    </Link>
                </div>
                <div className="login-logo">
                    <img src={logo} alt="BNV Logo" />
                </div>
                <div className="login-mode" key={mode}>
                {mode === 'student' && <h2 className="login-title">학생 로그인</h2>}

                {mode === 'user' ? (
                    <>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                                <Input
                                    type="email"
                                    placeholder="이메일"
                                    {...register('email')}
                                    error={errors.email?.message}
                                />
                                <Input
                                    type="password"
                                    placeholder="비밀번호"
                                    {...register('password')}
                                    error={errors.password?.message}
                                />

                                {error && <Alert severity="danger" variant="filled" size="sm">{error}</Alert>}

                                <div style={{ textAlign: 'right' }}>
                                    <Link to="/forgot-password" className="forgot-password-link">
                                        비밀번호를 잊어버리셨나요?
                                    </Link>
                                </div>

                                <Button variant="primary" type="submit" loading={isSubmitting} style={{ width: '100%' }}>
                                    로그인
                                </Button>
                            </div>
                        </form>

                        <div className="login-divider"><span>또는</span></div>

                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => setMode('student')}
                            style={{ width: '100%' }}
                        >
                            학생 로그인
                        </Button>

                        <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
                            <span>아직 멘토 계정이 없으신가요? </span>
                            <Link to="/signup" className="signup-link">멘토 회원가입</Link>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="login-subtitle">교사에게 받은 수업 코드로 입장하세요.</p>

                        <StudentLoginForm />

                        <div className="login-divider"><span>또는</span></div>

                        <Button
                            variant="secondary"
                            type="button"
                            onClick={() => setMode('user')}
                            style={{ width: '100%' }}
                        >
                            일반 로그인
                        </Button>
                    </>
                )}
                </div>
            </div>

            <ConsentModal
                open={!!consentState}
                group={consentState?.policyGroup || 'instructor'}
                submitting={consentSubmitting}
                onAgree={handleConsentAgree}
                onClose={() => setConsentState(null)}
            />
        </div>
    );
}

export default Login;
